'use client';

import { c as _c } from "react/compiler-runtime";
import type { AnswerState, AnswerValue } from '@repo/contracts/cardfinder/funnel/types';
import { useIdentityContext } from '@repo/platform-analytics/IdentityContext';
import { env } from '@repo/platform-env';
import { type Dispatch, type MutableRefObject, useCallback, useEffect, useRef } from 'react';
import type { AnswerAction } from '@/entities/answer/model/actions';
import type { AnswerStore } from '@/entities/answer/model/types';
import type { SessionSyncResult } from '@/entities/session/api/sessionWriteResult';
import type { BranchId } from '@/shared/config/funnel/constants';
import { ENTRY_STEP_ID } from '@/shared/config/funnel/flowConstants';
import { saveFunnelSession } from '../api/saveFunnelSession';
import { readLocalSession, resetLocalSession, shouldRotateLocalSession } from './localSession';
import { useLocalAnswerHydration } from './useLocalAnswerHydration';
type UseAnswerSubmissionSyncParams = {
  branchId: BranchId | null;
  store: AnswerStore;
  dispatch: Dispatch<AnswerAction>;
  hasSyncedOnLoginRef: MutableRefObject<boolean>;
};

/**
 * Owns session syncing *while the user is working through the funnel*, and
 * nothing else. It never writes `status: 'complete'` — finishing a run belongs
 * to `useCompleteFunnelRun` — so it is inert once the funnel is over.
 */
export function useAnswerSubmissionSync(t0) {
  const $ = _c(15);
  const {
    branchId,
    store,
    dispatch,
    hasSyncedOnLoginRef
  } = t0;
  const isSessionPersistenceEnabled = env.NEXT_PUBLIC_ENABLE_SESSION_PERSISTENCE;
  const {
    isLoggedIn
  } = useIdentityContext();
  const primaryGoalRef = useRef(undefined);
  let t1;
  if ($[0] !== branchId || $[1] !== dispatch || $[2] !== store) {
    t1 = {
      branchId,
      store,
      dispatch
    };
    $[0] = branchId;
    $[1] = dispatch;
    $[2] = store;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  useLocalAnswerHydration(t1);
  let t2;
  let t3;
  if ($[4] !== store.answers || $[5] !== store.isHydrated) {
    t2 = () => {
      if (!store.isHydrated || primaryGoalRef.current !== undefined) {
        return;
      }
      primaryGoalRef.current = store.answers[ENTRY_STEP_ID];
    };
    t3 = [store.isHydrated, store.answers];
    $[4] = store.answers;
    $[5] = store.isHydrated;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== branchId || $[9] !== hasSyncedOnLoginRef || $[10] !== isLoggedIn || $[11] !== store.isHydrated) {
    t4 = async (submittedAnswers, funnelConfigVersion) => {
      if (!store.isHydrated) {
        return "skipped";
      }
      if (shouldRotateLocalSession(readLocalSession())) {
        resetLocalSession();
      }
      const primaryGoal = submittedAnswers[ENTRY_STEP_ID];
      if (primaryGoalRef.current !== undefined && primaryGoalRef.current !== primaryGoal) {
        resetLocalSession();
        hasSyncedOnLoginRef.current = false;
      }
      primaryGoalRef.current = primaryGoal;
      if (!isSessionPersistenceEnabled || !isLoggedIn || !branchId) {
        return "skipped";
      }
      if (Object.keys(submittedAnswers).length === 0) {
        return "skipped";
      }
      hasSyncedOnLoginRef.current = true;
      const {
        result
      } = await saveFunnelSession(submittedAnswers, {
        funnelConfigVersion
      });
      return result;
    };
    $[8] = branchId;
    $[9] = hasSyncedOnLoginRef;
    $[10] = isLoggedIn;
    $[11] = store.isHydrated;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const persistSubmittedAnswer = t4;
  let t5;
  if ($[13] !== persistSubmittedAnswer) {
    t5 = {
      persistSubmittedAnswer
    };
    $[13] = persistSubmittedAnswer;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}